import React from "react";
// Meter Event Report page

// Default
import { useState } from "react";

// Mui
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

// Custom
import Layout from "../../layout";
import ContainedButton from "../../forms/button/ContainedButton";
import CustomDataTable from "../../components/table/CustomTable";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import MeterEventReportPieChart from "../charts/MeterEventReportPieChart";

const allData = [
  {
    eventid: "EV001",
    meterid: "123456789",
    customerid: "987654321",
    eventtype: "Power Outage",
    eventtime: "2024-05-07 08:15",
    severity: "High",
    status: "Resolved",
    operatornotes: "Restored remotely",
  },
  {
    eventid: "EV002",
    meterid: "987654321",
    customerid: "123456789",
    eventtype: "Tamper Alert",
    eventtime: "2024-05-08 09:20",
    severity: "Medium",
    status: "Pending",
    operatornotes: "Investigation ongoing",
  },
  {
    eventid: "EV003",
    meterid: "456789123",
    customerid: "654987321",
    eventtype: "Low Battery",
    eventtime: "2024-05-09 10:30",
    severity: "Low",
    status: "Resolved",
    operatornotes: "Battery replaced",
  },
];

const MeterEventReport: React.FC = () => {
  const [selectedEventType, setSelectedEventType] = useState({
    label: "All",
    value: "All",
  });
  const [filteredData, setFilteredData] = useState(allData);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const options = [
    { label: "All", value: "All" },
    { label: "Power Outage", value: "Power Outage" },
    { label: "Tamper Alert", value: "Tamper Alert" },
    { label: "Low Battery", value: "Low Battery" },
  ];

  const downloadCSV = () => {
    const headers = Object.keys(filteredData[0]).join(",");
    const rows = filteredData
      .map((row) => Object.values(row).join(","))
      .join("\n");
    const csvContent = `${headers}\n${rows}`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${window.location.pathname.slice(1)}.csv`;
    link.click();

    URL.revokeObjectURL(link.href);
  };

  const handleEventTypeChange = (event: any) => {
    setSelectedEventType(event);

    setFilteredData(
      event.value === "All"
        ? allData
        : allData?.filter((item) => item.eventtype === event.value)
    );
  };

  const COLUMNS = [
    "Event Id",
    "Meter Id",
    "Customer Id",
    "Event Type",
    "Event Time",
    "Severity",
    "Status",
    "Operator Notes",
  ];

  const rowsContent = filteredData?.map((item, index) => (
    <TableRow
      key={index}
      sx={{
        height: "50px",
        background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
      }}
      className="custom_table_tbody"
    >
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.eventid}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.meterid}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.customerid}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.eventtype}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.eventtime}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.severity}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.status}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.operatornotes}
        </Typography>
      </TableCell>
    </TableRow>
  ));

  return (
    <Layout>
      <Card className="meters_title_card">
        <Typography variant="h5">Meter Events Report</Typography>
      </Card>

      <Card sx={{ mt: "20px", maxHeight: "400px" }}>
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="h5">Filter By Status Type</Typography>
              <CustomOutlinedSelect
                placeholder={"Select Status"}
                options={options}
                width="fit-content"
                value={selectedEventType}
                onChange={(newValue) => handleEventTypeChange(newValue)}
              />
            </Stack>
          }
        />
        <Divider />

        <CardContent>
          <MeterEventReportPieChart data={filteredData} />
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="h5">Report Details</Typography>
              <ContainedButton
                label={"Download CSV"}
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  color: "#ffffff",
                }}
                onClick={downloadCSV}
              />
            </Stack>
          }
        />
        <Divider />

        <CardContent sx={{ overflow: "auto", maxHeight: "600px" }}>
          <CustomDataTable
            columns={COLUMNS}
            pagination={true}
            rowsContent={rowsContent}
            Actions={true}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            page={page}
            onRowsPerPageChange={handleChangeRowsPerPage}
            totalCount={filteredData?.length}
          />
        </CardContent>
      </Card>
    </Layout>
  );
};

export default MeterEventReport;
